import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, XAxis } from 'recharts';
import { cn } from '@r/utils/utils.ts';
import { BarChartSkeleton } from '@r/components/kpi/skeleton/bar-chart-skeleton.tsx';

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@r/components/ui/chart.tsx';

export type FormattedDataForRendering = { date: string; data: number; fill: string };

export const BarChartBloc = ({
  formattedDataPerMonth,
  dataType,
  classname,
  loading = false
}: {
  formattedDataPerMonth: FormattedDataForRendering[];
  dataType?: 'point' | 'monetary' | 'quantity';
  classname?: string;
  loading?: boolean;
}) => {
  const { t } = useTranslation();

  if (loading) {
    return <BarChartSkeleton />;
  }

  const chartConfig = {
    data: {}
  } satisfies ChartConfig;

  return (
    <ChartContainer
      config={chartConfig}
      className={cn('w-full h-full', classname)}
      aspectClassName="aspect-auto"
    >
      <BarChart barSize={30} accessibilityLayer data={formattedDataPerMonth}>
        <XAxis
          dataKey="date"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          tickFormatter={(value) => value.slice(0, 3)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideIndicator={true}
              formatter={(value) => {
                switch (dataType) {
                  case 'point':
                    return typeof value === 'number'
                      ? pointsTranslation(t, value, true)
                      : `${value} ${t('point.name_other')}`;
                  case 'monetary':
                    return `${value} €`;
                  case 'quantity':
                    return `${value}`;
                  default:
                    return value;
                }
              }}
            />
          }
          cursor={false}
        />
        <Bar minPointSize={30} dataKey="data" radius={15} />
      </BarChart>
    </ChartContainer>
  );
};
